/**
 * @lines98/client/lib/identity-events — тонкий pub/sub-seam «идентичность сменилась»
 * (account-profile-redesign.md §2.3 / §9).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «ствол → ветки»): после login /
 * register / logout несколько поверхностей должны reconcile'нуться БЕЗ перезагрузки
 * страницы — account-slot рейла (пилюля ↔ chip, §2.3) И открытый Профиль (мета-секции
 * + конверсионный баннер, §9). Раньше Профиль сам держал auth-panel и сам reconcile'ил;
 * теперь вход/выход живут вне Профиля (рейл-slot + auth-modal + меню chip), поэтому
 * нужен ОДИН общий сигнал, на который подписываются заинтересованные поверхности. Это
 * НЕ копия логики по экранам — единый seam (как `lib/session-revoked`).
 *
 * SCOPE: `emitIdentityChanged()` (после смены identity — login/register/logout) +
 * `onIdentityChanged(cb)` (подписка, возвращает unsubscribe). Без состояния — чистый
 * брокер; идентичность читается подписчиком из `services/identity` синхронно.
 *
 * NOT IN SCOPE: сам logout/login (вызывающий уже сменил identity ДО emit), хранение
 * identity (это `services/identity`).
 *
 * Cross-references:
 *   - account-profile-redesign.md §2.3 (реактивность slot), §9 (edge: выход на Профиле).
 *   - app.ts (emit из shared logout + auth-modal success), screens/profile-screen (подписка).
 *   - lib/session-revoked.ts (sibling pub/sub-seam — тот же паттерн).
 */

type IdentityChangedListener = () => void;

const listeners = new Set<IdentityChangedListener>();

/**
 * Подписаться на сигнал «идентичность сменилась». Возвращает unsubscribe. Подписчик
 * (напр. открытый Профиль) reconcile'ит свой вид из свежей identity при срабатывании.
 */
export function onIdentityChanged(cb: IdentityChangedListener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Опубликовать «идентичность сменилась» (login / register / logout). Вызывается ПОСЛЕ
 * того, как `services/identity` уже обновлён (re-mint анона / залогинен), чтобы
 * подписчики читали свежую правду. Ошибка в одном подписчике не валит остальных.
 */
export function emitIdentityChanged(): void {
  for (const cb of [...listeners]) {
    try {
      cb();
    } catch {
      // best-effort: один подписчик не должен ронять остальные reconcile'ы.
    }
  }
}
